import { ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const convertBytesToMB = (bytes: number) => {
  return (bytes / (1024 * 1024)).toFixed(2);
};

export const formatDateString = (date_string: string) => {
  const inputDate = new Date(date_string);

  const day = inputDate.getDate().toString().padStart(2, '0');
  const month = (inputDate.getMonth() + 1).toString().padStart(2, '0');
  const year = inputDate.getFullYear().toString().slice(-2);

  return day + '/' + month + '/' + year;
};

export const SITE_REGION_CODES = ['au', 'nz', 'us', 'in'] as const;
export type SiteRegionCode = (typeof SITE_REGION_CODES)[number];

/** Regions currently blocked / redirected to coming soon (e.g. prelaunch). */
export const BLOCKED_REGION_CODES: readonly SiteRegionCode[] = ['us'] as const;

/** Regions served under unimig.com/{code} (excludes AU, which uses unimig.com.au). */
export const MULTI_REGION_SITE_CODES = ['nz', 'us', 'in'] as const;

export const COUNTRY_LOCALE_MAP: Record<string, string> = {
  au: 'en-AU',
  nz: 'en-NZ',
  us: 'en-US',
  in: 'en-IN',
};

export { getRegionalUrl, localizeHtmlLinks } from './regionalUrl';
export {
  buildRegionalPageUrl,
  buildHreflangLanguages,
  getStaticPageHreflang,
  type RegionalVariant,
} from './hreflang';

export const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  au: 'AUD',
  nz: 'NZD',
  us: 'USD',
  in: 'INR',
};

export const DEFAULT_CURRENCY = 'AUD';

/**
 * Returns the 3-letter ISO 4217 currency code for a given country code or currency code.
 * Defaults to 'AUD' if unknown or not provided.
 */
export function getCurrencyForCountry(countryCode?: string | null): string {
  if (!countryCode) return DEFAULT_CURRENCY;
  const normalized = countryCode.toLowerCase().trim();
  if (COUNTRY_CURRENCY_MAP[normalized]) {
    return COUNTRY_CURRENCY_MAP[normalized];
  }
  const upper = countryCode.toUpperCase().trim();
  if (Object.values(COUNTRY_CURRENCY_MAP).includes(upper)) {
    return upper;
  }
  return DEFAULT_CURRENCY;
}

/**
 * Returns the base URL for the given country/region code:
 * - 'au' (or single region) -> NEXT_PUBLIC_SINGLE_REGION_URL (e.g. 'https://unimig.com.au')
 * - 'nz', 'us', 'in' (multi-region) -> NEXT_PUBLIC_WEBSITE_URL/{countryCode} (e.g. 'https://unimig.com/nz')
 */
export function getSiteBaseUrl(countryCode?: string | null): string {
  const singleRegionUrl = (
    process.env.NEXT_PUBLIC_SINGLE_REGION_URL || 'https://unimig.com.au'
  ).replace(/\/+$/, '');
  const multiRegionDomain = (
    process.env.NEXT_PUBLIC_WEBSITE_URL || 'https://unimig.com'
  ).replace(/\/+$/, '');

  const singleRegion = (process.env.NEXT_PUBLIC_SITE_REGION || 'au')
    .toLowerCase()
    .trim();

  const code = (countryCode || singleRegion).toLowerCase().trim();

  if (code === singleRegion || code === 'au') {
    return singleRegionUrl;
  }

  return `${multiRegionDomain}/${code}`;
}
